import React, { useMemo, useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import {
  deactivateLocation,
  getLocationDetails,
} from '../../services/locationService';
import type { LocationDetails } from '../../types/location';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { organizationErrorMessage } from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'LocationDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

export function LocationDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
        marginTop: spacing.md,
      },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const { locationId } = route.params;
  const [item, setItem] = useState<LocationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const data = await getLocationDetails(locationId);
        setItem(data);
        setError(null);
      } catch (caught) {
        setItem(null);
        setError(
          organizationErrorMessage(
            caught,
            'The requested record could not be loaded.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [locationId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.LOCATION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Location"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading location" />
        </AppScreen>
      ) : error && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Location"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : item ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title={item.locationName}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {capabilities.canUpdateLocation &&
            isActiveConfigurationRecord(item.isActive) ? (
              <ConfigurationDeleteButton
                entityLabel="location"
                loading={softDelete.busy}
                disabled={softDelete.busy}
                onPress={() =>
                  softDelete.confirmAndDelete({
                    entityLabel: 'location',
                    recordName: item.locationName,
                    deleteRecord: async () => {
                      await deactivateLocation(item);
                    },
                    onSuccess: () => navigation.goBack(),
                    errorMessage: caught =>
                      organizationErrorMessage(
                        caught,
                        'Unable to delete location.',
                      ),
                  })
                }
              />
            ) : null}
            <Field palette={palette} label="Code" value={item.locationCode} />
            <Field palette={palette} label="Type" value={item.locationType} />
            <Field palette={palette} label="Address line 1" value={item.addressLine1} />
            <Field palette={palette} label="Address line 2" value={item.addressLine2} />
            <Field palette={palette} label="City" value={item.city} />
            <Field palette={palette} label="State" value={item.stateName} />
            <Field palette={palette} label="Country" value={item.countryCode} />
            <Field palette={palette} label="Postal code" value={item.postalCode} />
            <Field palette={palette} label="Timezone" value={item.timeZone} />
            <Field
              palette={palette}
              label="Latitude"
              value={item.latitude != null ? String(item.latitude) : null}
            />
            <Field
              palette={palette}
              label="Longitude"
              value={item.longitude != null ? String(item.longitude) : null}
            />
            <Field palette={palette} label="Contact person" value={item.contactPerson} />
            <Field palette={palette} label="Contact number" value={item.contactNumber} />
            <Field
              palette={palette}
              label="Head office"
              value={item.isHeadOffice ? 'Yes' : 'No'}
            />
            <Field
              palette={palette}
              label="Attendance enabled"
              value={item.isAttendanceEnabled ? 'Yes' : 'No'}
            />
            <Field palette={palette} label="Status" value={item.statusLabel} />
          </AppCard>
          <View style={styles.actions}>
            {capabilities.canUpdateLocation ? (
              <AppButton
                title="Edit"
                onPress={() =>
                  navigation.navigate(routes.locationEdit, { locationId })
                }
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.md },
  field: { gap: spacing.xs },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
});
